// @ts-strict-ignore
import {
  DEFAULT_OUTCOME_COLOR_IN_HEX_FORMAT,
  DEFAULT_OUTCOME_ICON_NAME,
} from '~/dashboard/views/workflows/editor/components/constants';
import { ActionType } from '../../constants';
import type { Action } from '../../types';
import { generatePublicId } from '../../utils';
import { OUTPUT_ATTRIBUTE_DEFAULT_EXPRESSION_BY_DATA_TYPE } from './constants';
import { LIST_SERVICE_KEY_IN_SERVICES, CUSTOM_LIST_SERVICE_KEY_IN_SERVICES } from 'flow/service/services/constants';
import { buildIndexableActionOptionEntry, indexEntriesIntoRecord } from './helpers';
import type {
  CreateActionPayload,
  CreateImportedMatrixModelActionArgs,
  CreateImportedOutcomeReasonActionArgs,
  CreateImportedOutputAttributeActionArgs,
  CreateInputAttributeActionArgs,
  CreateMatrixModelActionArgs,
  CreateOutcomeActionArgs,
  CreateOutputAttributeActionArgs,
  CreatePreviewActionArgs,
  CreateServiceActionArgs,
  CreateTagActionArgs,
  CreateWorkflowRoutingActionArgs,
} from './types';
import {
  DEFAULT_OUTCOME_PRECEDENCE_INDEX,
  DEFAULT_INIT_AXIS,
  DEFAULT_NULL_X_AXIS,
  getFormattedMatrixDataType,
  generateTemporaryMatrixModelToken,
} from '~/dashboard/views/workflows/service/actions';
import { isValidPublishedAttribute } from 'flow/service/publishedAttributes';

export function buildBaseAction(type: ActionType, defaults?: Pick<Action, 'publicId' | 'rules'>) {
  const { publicId, rules = {} } = defaults ?? {};
  const baseAction: Pick<
  Action,
  'options' | 'type' | 'serviceFailureDefault' | 'publicId' | 'rules'
  > = {
    options: {},
    publicId: publicId ?? generatePublicId('action', type),
    // null will be interpreted as "exit workflow" which should always be the default
    serviceFailureDefault: null,
    type,
    rules,
  };
  return baseAction;
}

function buildServiceAction(args: CreateServiceActionArgs): Action {
  const {
    name, internalName, publicId, listId, listType,
  } = args;

  const serviceAction: Action = {
    ...buildBaseAction(ActionType.SERVICE, { publicId }),
    service: {
      internalName,
      name,
    },
  };

  if (listId) {
    switch (internalName) {
      case LIST_SERVICE_KEY_IN_SERVICES:
        if (listType === 'whitelist') {
          serviceAction.service.isWhitelist = true;
        } else if (listType === 'blacklist') {
          serviceAction.service.isBlacklist = true;
        }
        serviceAction.options = indexEntriesIntoRecord([
          buildIndexableActionOptionEntry('customer_list_id', { value: listId }),
        ]);
        break;
      case CUSTOM_LIST_SERVICE_KEY_IN_SERVICES:
        serviceAction.options = indexEntriesIntoRecord([
          buildIndexableActionOptionEntry('custom_list_token', { value: listId }),
          ...(
            args.listMajorVersion
              ? [buildIndexableActionOptionEntry('custom_list_major_version', { value: String(args.listMajorVersion) })]
              : []
          ),
        ]);
        break;
    }
  }


  return serviceAction;
}

function buildTagAction(args: CreateTagActionArgs): Action {
  const { publicId, rules, ...tagFields } = args;

  // todo BUILD-653 - if this data is available in props, it should be passed in at `args`
  return {
    ...buildBaseAction(ActionType.TAG, { publicId, rules }),
    tag: tagFields,
  };
}

function buildInputAttributeAction(args: CreateInputAttributeActionArgs): Action {
  const {
    dataType,
    jqFunction,
    name,
    publicId,
    publishedAttributeToken = null,
    useJq,
    publishedDetails,
    isPublished,
  } = args;
  // todo BUILD-653 - if this data is available in props, it should be passed in at `args`
  const actionOptions = indexEntriesIntoRecord([
    buildIndexableActionOptionEntry('name', { value: name }),
    buildIndexableActionOptionEntry('data_type', { value: dataType }),
    buildIndexableActionOptionEntry('use_jq', { value: `${useJq}` }),
    buildIndexableActionOptionEntry('jq_function', { value: jqFunction }),
  ]);

  return {
    ...buildBaseAction(ActionType.INPUT, { publicId }),
    input: {
      actionOptionPublicId: generatePublicId('actionoption'),
      dataType,
      name,
      publishedAttributeToken,
      useJq,
      jqFunction,
      ...(isPublished && isValidPublishedAttribute(publishedDetails) && { publishedDetails }),
    },
    options: actionOptions,
  };
}

function buildOutputAttributeAction(args: CreateOutputAttributeActionArgs): Action {
  const {
    category,
    dataType,
    isPublished,
    jqFunction,
    name,
    outputValueRange,
    publicId,
    publishedAttributeToken,
    publishedDetails,
  } = args;

  // todo BUILD-653 - if this data is available in props, it should be passed in at `args`
  const jsonValue = [...OUTPUT_ATTRIBUTE_DEFAULT_EXPRESSION_BY_DATA_TYPE[dataType]];

  const actionOptions = indexEntriesIntoRecord([
    buildIndexableActionOptionEntry('name', { value: name }),
    buildIndexableActionOptionEntry('data_type', { value: dataType }),
    buildIndexableActionOptionEntry('use_jq', { value: 'false' }),
    buildIndexableActionOptionEntry('expression', { value: JSON.stringify(jsonValue), jsonValue }),
    ...(isPublished && category ? [buildIndexableActionOptionEntry('category', { value: category })] : []),
    ...(isPublished ? [buildIndexableActionOptionEntry('jq_function', { value: jqFunction })] : []),
    ...(isPublished && outputValueRange
      ? [buildIndexableActionOptionEntry('output_value_range', { value: outputValueRange, jsonValue: outputValueRange })]
      : []
    ),
  ]);

  return {
    ...buildBaseAction(ActionType.OUTPUT, { publicId }),
    output: {
      actionOptionPublicId: generatePublicId('actionoption'),
      expression: [],
      name,
      publishedAttributeToken,
      useJq: 'false',
      jqFunction,
      dataType,
      ...(isPublished && isValidPublishedAttribute(publishedDetails) && { publishedDetails }),
    },
    options: actionOptions,
  };
}

function buildImportedOutcomeReasonAction(args: CreateImportedOutcomeReasonActionArgs): Action {
  const { color: tagColor, name, publicId } = args;

  const actionOptionName = `.imported.outcome_reasons | map(.name) | index("${name}") != null`;
  // todo BUILD-653 - if this data is available in props, it should be passed in at `args`
  const actionOptions = indexEntriesIntoRecord([
    buildIndexableActionOptionEntry('name', { value: actionOptionName }),
    buildIndexableActionOptionEntry('displayName', { value: name }),
    buildIndexableActionOptionEntry('data_type', { value: 'boolean' }),
    buildIndexableActionOptionEntry('use_jq', { value: 'true' }),
  ]);

  return {
    ...buildBaseAction(ActionType.OUTCOME_REASON, { publicId }),
    outcomeReason: {
      color: tagColor,
      actionOptionPublicId: generatePublicId('actionoption'),
      dataType: 'boolean',
      displayName: name,
      name: actionOptionName,
      useJq: 'true',
    },
    options: actionOptions,
  };
}

function buildImportedOutputAttributeAction(args: CreateImportedOutputAttributeActionArgs): Action {
  const { dataType, name, publicId } = args;
  const actionOptionName = `.imported.output_attributes | .[] | select(.name=="${name}") | .value`;

  // todo BUILD-653 - if this data is available in props, it should be passed in at `args`
  const actionOptions = indexEntriesIntoRecord([
    buildIndexableActionOptionEntry('name', { value: actionOptionName }),
    buildIndexableActionOptionEntry('displayName', { value: name }),
    buildIndexableActionOptionEntry('data_type', { value: dataType }),
    buildIndexableActionOptionEntry('use_jq', { value: 'true' }),
  ]);

  return {
    ...buildBaseAction(ActionType.IMPORTED_OUTPUT_ATTRIBUTE, { publicId }),
    importedOutputAttribute: {
      actionOptionPublicId: generatePublicId('actionoption'),
      dataType,
      displayName: name,
      name: actionOptionName,
      useJq: 'true',
    },
    options: actionOptions,
  };
}

function buildImportedMatrixModelAction(args: CreateImportedMatrixModelActionArgs): Action {
  const { name, publicId, dataType } = args;
  const actionOptionName = `.imported.matrix_models | .[] | select(.name=="${name}") | .value`;

  const formattedDataType = getFormattedMatrixDataType(dataType);

  // todo BUILD-653 - if this data is available in props, it should be passed in at `args`
  const actionOptions = indexEntriesIntoRecord([
    buildIndexableActionOptionEntry('name', { value: actionOptionName }),
    buildIndexableActionOptionEntry('displayName', { value: name }),
    buildIndexableActionOptionEntry('data_type', { value: formattedDataType }),
    buildIndexableActionOptionEntry('use_jq', { value: 'true' }),
  ]);

  return {
    ...buildBaseAction(ActionType.IMPORTED_MATRIX_MODEL, { publicId }),
    importedMatrixModel: {
      actionOptionPublicId: generatePublicId('actionoption'),
      dataType: formattedDataType,
      displayName: name,
      name: actionOptionName,
      useJq: 'true',
    },
    options: actionOptions,
  };
}

function buildMatrixModelAction(args: CreateMatrixModelActionArgs): Action {
  const {
    name, dataType, description, dimensions, publicId,
  } = args;
  const is2Dimensional = dimensions === 2;

  const actionOptions = indexEntriesIntoRecord([
    buildIndexableActionOptionEntry('description', { value: description }),
  ]);

  return {
    ...buildBaseAction(ActionType.MATRIX_MODEL, { publicId }),
    matrixModel: {
      name,
      actionOptionPublicId: generatePublicId('actionoption'),
      matrixDataType: dataType,
      activeMatrixModelVersionId: null,
      dataRouteX: null,
      dataRouteY: null,
      matrixModelPublicVersion: null,
      matrixModelId: null,
      matrixModelToken: generateTemporaryMatrixModelToken(),
      axis: {
        x: is2Dimensional ? DEFAULT_INIT_AXIS : DEFAULT_NULL_X_AXIS,
        y: DEFAULT_INIT_AXIS,
      },
      matrix: [[null]],
      // TODO 3888 init?
    },
    options: actionOptions,
  };
}

function buildOutcomeAction(args: CreateOutcomeActionArgs): Action {
  const {
    name,
    isCaseStatus,
    color = DEFAULT_OUTCOME_COLOR_IN_HEX_FORMAT,
    icon = DEFAULT_OUTCOME_ICON_NAME,
    precedenceIndex = DEFAULT_OUTCOME_PRECEDENCE_INDEX,
    publicId,
    rules = {},
    isEphemeral,
  } = args;

  return {
    ...buildBaseAction(ActionType.OUTCOME, { publicId, rules }),
    outcome: {
      color,
      icon,
      isCaseStatus,
      name,
      precedenceIndex,
      isEphemeral,
    },
  };
}

function buildPreviewAction(args: CreatePreviewActionArgs): Action {
  const { publicId } = args;
  return {
    ...buildBaseAction(ActionType.PREVIEW, { publicId }),
  };
}

function buildWorkflowRoutingAction(args: CreateWorkflowRoutingActionArgs): Action {
  const {
    publicId, name, precedenceIndex, workflowId,
  } = args;
  return {
    ...buildBaseAction(ActionType.WORKFLOW, { publicId }),
    workflow: {
      name,
      precedenceIndex,
      workflowId,
    },
  };
}

/**
 * Factory function that builds an Action based on the type and args supplied
 * @param payload object containing the type and arguments for the action
 * @returns an Action of type `type`
 */
export function createAction({ type, args }: CreateActionPayload): Action {
  switch (type) {
    case ActionType.SERVICE:
      return buildServiceAction(args);
    case ActionType.TAG:
      return buildTagAction(args);
    case ActionType.INPUT:
      return buildInputAttributeAction(args);
    case ActionType.MATRIX_MODEL:
      return buildMatrixModelAction(args);
    case ActionType.OUTPUT:
      return buildOutputAttributeAction(args);
    case ActionType.OUTCOME_REASON:
      return buildImportedOutcomeReasonAction(args);
    case ActionType.IMPORTED_OUTPUT_ATTRIBUTE:
      return buildImportedOutputAttributeAction(args);
    case ActionType.IMPORTED_MATRIX_MODEL:
      return buildImportedMatrixModelAction(args);
    case ActionType.OUTCOME:
      return buildOutcomeAction(args);
    case ActionType.PREVIEW:
      return buildPreviewAction(args);
    case ActionType.WORKFLOW:
      return buildWorkflowRoutingAction(args);
    default:
      throw new Error(`Cannot build action with unknown type: ${type}`);
  }
}
